import React, { useCallback, useEffect, useState } from "react";
import { 
    Calendar, 
    Clock,
    Search,
    Check,
    X,
    MessageSquare
} from "lucide-react";
import api from "../../api/axios";

const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const TeamLeave = () => {
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");
    const [search, setSearch] = useState("");
    const [processing, setProcessing] = useState(null);

    const fetchLeaves = useCallback(async () => {
        try {
            const res = await api.get("/api/employee/team/leave");
            setLeaves(res.data);
        } catch (err) {
            console.error("Failed to load team leaves:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLeaves();
    }, [fetchLeaves]);

    const handleAction = async (id, status) => {
        const leave = leaves.find((item) => item._id === id);
        if (status === "approved") {
            const conflicts = getLeaveConflicts(leave, leaves);
            if (conflicts.length) {
                const names = conflicts.map((item) => item.employee?.name).filter(Boolean).join(", ");
                const proceed = window.confirm(`Leave conflict alert: ${names || conflicts.length + " teammate(s)"} already have approved leave in this period. Approve anyway?`);
                if (!proceed) return;
            }
        }

        let rejectionReason = "";
        if (status === "rejected") {
            rejectionReason = prompt("Reason for rejection:");
            if (rejectionReason === null) return;
        }

        const previousLeaves = leaves;
        const processedAt = new Date().toISOString();
        setLeaves((current) =>
            current.map((leave) =>
                leave._id === id
                    ? { ...leave, status, rejectionReason, processedAt }
                    : leave,
            ),
        );
        setProcessing(id);
        try {
            const res = await api.patch(`/api/employee/team/leave/${id}`, { status, rejectionReason });
            setLeaves((current) => current.map((leave) => leave._id === id ? res.data : leave));
            window.setTimeout(() => fetchLeaves(), 0);
        } catch (err) {
            setLeaves(previousLeaves);
            alert("Failed to update status");
        } finally {
            setProcessing(null);
        }
    };

    const filteredLeaves = leaves.filter(l => {
        const matchesStatus = filter === "all" || l.status === filter;
        const matchesSearch = l.employee?.name?.toLowerCase().includes(search.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });

    if (loading) return <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <div className="max-w-7xl mx-auto p-6 space-y-6">
                
                {/* HERO */}
                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] shadow-[var(--shadow-soft)] rounded-3xl p-8">
                    <h1 className="text-4xl font-bold">Team Leave Requests</h1>
                    <p className="mt-3 text-lg text-[var(--text-secondary)]">Manage leave applications from your team members</p>
                </div>

                {/* FILTERS */}
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
                        <input 
                            placeholder="Search employee..." 
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full bg-[var(--card-bg)] border border-[var(--border-color)] pl-11 pr-4 py-3 rounded-2xl outline-none"
                        />
                    </div>
                    <div className="flex gap-2 bg-[var(--card-bg)] p-1.5 border border-[var(--border-color)] rounded-2xl overflow-x-auto">
                        {["pending", "approved", "rejected", "all"].map(s => (
                            <button
                                key={s}
                                onClick={() => setFilter(s)}
                                className={`px-5 py-2 rounded-xl text-sm font-medium capitalize transition-all whitespace-nowrap ${filter === s ? "bg-[var(--accent-primary)] text-white" : "hover:bg-[var(--bg-secondary)]"}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                {/* LIST */}
                <div className="grid gap-4">
                    {filteredLeaves.length === 0 ? (
                        <div className="bg-[var(--card-bg)] border border-[var(--border-color)] p-12 rounded-3xl text-center">
                            <Clock size={48} className="mx-auto text-[var(--text-secondary)] mb-4 opacity-20" />
                            <p className="text-lg font-medium text-[var(--text-secondary)]">No requests found</p>
                        </div>
                    ) : (
                        filteredLeaves.map(l => (
                            <div key={l._id} className="bg-[var(--card-bg)] border border-[var(--border-color)] p-6 rounded-3xl shadow-[var(--shadow-soft)] flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                                <div className="flex items-center gap-4 min-w-[250px]">
                                    <div className="w-12 h-12 rounded-2xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center font-bold text-lg">
                                        {l.employee?.name?.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg">{l.employee?.name}</h3>
                                        <p className="text-xs text-[var(--text-secondary)] capitalize">{l.leaveType} Leave</p>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Calendar size={14} className="text-[var(--accent-primary)]" />
                                        <span className="font-medium">{formatDate(l.startDate)} - {formatDate(l.endDate)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                                        <MessageSquare size={14} />
                                        <span className="italic">"{l.reason}"</span>
                                    </div>
                                    {l.rejectionReason && (
                                            <div className="text-xs text-red-500 font-semibold mt-1">
                                                Rejection Note: "{l.rejectionReason}"
                                            </div>
                                        )}
                                    {l.status === "pending" && getLeaveConflicts(l, leaves).length > 0 && (
                                        <div className="mt-2 rounded-xl border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-600">
                                            Conflict: {getLeaveConflicts(l, leaves).map((item) => item.employee?.name).filter(Boolean).join(", ")} already approved.
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-3">
                                    {l.status === "pending" ? (
                                        <>
                                            <button 
                                                disabled={processing === l._id}
                                                onClick={() => handleAction(l._id, "approved")}
                                                className="bg-emerald-500 hover:bg-emerald-600 text-white p-3 rounded-2xl transition-all disabled:opacity-50"
                                                title="Approve"
                                            >
                                                <Check size={20} />
                                            </button>
                                            <button 
                                                disabled={processing === l._id}
                                                onClick={() => handleAction(l._id, "rejected")}
                                                className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-2xl transition-all disabled:opacity-50"
                                                title="Reject"
                                            >
                                                <X size={20} />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-end gap-1">
                                            <div className={`px-4 py-2 rounded-full border text-xs font-bold uppercase ${l.status === "approved" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-red-500/10 text-red-500 border-red-500/20"}`}>
                                                {l.status}
                                            </div>
                                            {l.processedBy && (
                                                <span className="text-[10px] text-[var(--text-secondary)] font-semibold mt-0.5">
                                                    By: {l.processedBy.name} ({formatRoleLabel(l.processedBy.role) || "Admin"})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const rangesOverlap = (aStart, aEnd, bStart, bEnd) => {
    const startA = new Date(aStart).getTime();
    const endA = new Date(aEnd).getTime();
    const startB = new Date(bStart).getTime();
    const endB = new Date(bEnd).getTime();
    return startA <= endB && startB <= endA;
};

const getLeaveConflicts = (leave, leaves) => {
    if (!leave) return [];
    return leaves.filter((item) => (
        item._id !== leave._id
        && item.status === "approved"
        && String(item.employee?._id || "") !== String(leave.employee?._id || "")
        && rangesOverlap(leave.startDate, leave.endDate, item.startDate, item.endDate)
    ));
};

export default TeamLeave;
